import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { SwatchKind } from "@/components/primitives";
import { registerChannel, unregisterChannel } from "@/lib/realtimeChannels";

export interface Scan {
  id: string;
  user_id: string;
  title: string;
  brand: string | null;
  era: string | null;
  category: string | null;
  material: string | null;
  size: string | null;
  color: string | null;
  condition: string | null;
  condition_notes: string | null;
  verdict: "PASS" | "FAIL";
  verdict_label: string | null;
  confidence: number | null;
  rarity: string | null;
  tags: string[];
  notes: string[];
  img: SwatchKind;
  price_suggested: number | null;
  price_low: number | null;
  price_high: number | null;
  status: string;
  posted: boolean;
  posted_to: string | null;
  caption: string | null;
  sold_at: string | null;
  sold_price: number | null;
  created_at: string;
  updated_at: string;
  review_status: "pending" | "approved_closet" | "approved_feed" | "discarded";
}

export interface Grail {
  id: string;
  user_id: string;
  item: string;
  size: string | null;
  max_price: number | null;
  hits: number;
  created_at: string;
  updated_at: string;
  category?: string | null;
  size_system?: string | null;
  size_value?: string | null;
  size_width?: string | null;
  vintage_sizing?: boolean | null;
  waist_inches?: string | null;
  bust_chest_inches?: string | null;
  length_preference?: string | null;
  era_preference?: string[] | null;
  condition_preference?: string | null;
  style_preference?: string[] | null;
  hardware_preference?: string | null;
  material_preference?: string[] | null;
  jewellery_type?: string[] | null;
  ring_size?: string | null;
  necklace_length?: string | null;
  earring_style?: string | null;
  wrist_size?: string | null;
  metal_preference?: string | null;
  hat_size?: string | null;
  frame_size?: string | null;
  accessory_type?: string[] | null;
}

export interface Profile {
  id: string;
  handle: string;
  display_name: string | null;
  bio: string | null;
  location: string | null;
  avatar: SwatchKind;
  tags: string[];
  looking_for: string[];
  lat: number | null;
  lng: number | null;
  city: string | null;
  created_at: string;
  updated_at: string;
}

export function useScans() {
  const { user } = useAuth();
  const [scans, setScans] = useState<Scan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setScans([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    supabase
      .from("scans")
      .select("*")
      .eq("user_id", user.id)
      .not("review_status", "in", '("pending","discarded")')
      // Hide both 'processing' (still mid-flight or stranded) AND 'failed'
      // (verification errored out) — the closet should only show items the
      // user has a real verdict for. Failed rows stay in the DB so the
      // Result page can still surface the specific error when tapped via
      // a deep link or notification, but they don't clutter the grid.
      .not("processing_status", "in", '("processing","failed")')
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data) setScans(data as Scan[]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return { scans, loading, refresh: async () => {
    if (!user) return;
    const { data } = await supabase.from("scans").select("*").eq("user_id", user.id)
      .not("review_status", "in", '("pending","discarded")')
      .not("processing_status", "in", '("processing","failed")')
      .order("created_at", { ascending: false });
    if (data) setScans(data as Scan[]);
  } };
}

export function useGrails() {
  const { user } = useAuth();
  const [grails, setGrails] = useState<Grail[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!user) { setGrails([]); return; }
    const { data } = await supabase.from("grails").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
    if (data) setGrails(data as Grail[]);
  };

  useEffect(() => {
    if (!user) { setGrails([]); setLoading(false); return; }
    let cancelled = false;
    supabase.from("grails").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).then(({ data }) => {
      if (cancelled) return;
      if (data) setGrails(data as Grail[]);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user]);

  const add = async (input: Partial<Omit<Grail, "id" | "user_id" | "hits" | "created_at" | "updated_at">> & { item: string }) => {
    if (!user) return;
    const payload: Record<string, unknown> = { user_id: user.id };
    for (const [k, v] of Object.entries(input)) {
      if (v === undefined) continue;
      payload[k] = v;
    }
    const { data, error } = await supabase
      .from("grails")
      .insert(payload as never)
      .select()
      .single();
    if (!error && data) {
      setGrails((g) => [data as Grail, ...g]);
      // Fire-and-forget semantic backfill against all already-posted scans.
      supabase.functions.invoke("compute-grail-matches", {
        body: { kind: "grail", id: (data as Grail).id },
      }).catch((e) => console.warn("semantic backfill failed:", e));
    }
  };

  const remove = async (id: string) => {
    setGrails((g) => g.filter((x) => x.id !== id));
    await supabase.from("grails").delete().eq("id", id);
  };

  return { grails, loading, add, remove, refresh };
}

// A grail match joined with the seller's profile + the matched scan's preview info.
export interface GrailMatchRow {
  id: string;
  grail_id: string;
  scan_id: string;
  seller_id: string;
  matched_price: number | null;
  over_budget: boolean;
  seen: boolean;
  dismissed: boolean;
  created_at: string;
  // grail snapshot (for the badge label)
  grail_item: string;
  // scan snapshot
  scan_title: string;
  scan_brand: string | null;
  scan_era: string | null;
  scan_photo: string | null;   // first uploaded photo URL, may be null
  scan_img: string;            // swatch fallback
  // seller snapshot
  seller_handle: string;
  seller_display_name: string | null;
  seller_avatar: SwatchKind;
}

export function useGrailMatches() {
  const { user } = useAuth();
  const [matches, setMatches] = useState<GrailMatchRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) { setMatches([]); setLoading(false); return; }
    // 1. raw matches for this user
    const { data: rows } = await supabase
      .from("grail_matches")
      .select("*")
      .eq("grail_owner_id", user.id)
      .eq("dismissed", false)
      .order("created_at", { ascending: false })
      .limit(30);
    if (!rows || rows.length === 0) { setMatches([]); setLoading(false); return; }

    const grailIds  = Array.from(new Set(rows.map((r) => r.grail_id)));
    const scanIds   = Array.from(new Set(rows.map((r) => r.scan_id)));
    const sellerIds = Array.from(new Set(rows.map((r) => r.seller_id)));

    const [{ data: grailRows }, { data: scanRows }, { data: sellerRows }] = await Promise.all([
      supabase.from("grails").select("id,item").in("id", grailIds),
      supabase.from("scans").select("id,title,brand,era,photos,img,suspended_at").in("id", scanIds).is("suspended_at", null),
      (supabase as any).from("profiles_public").select("id,handle,display_name,avatar").in("id", sellerIds),
    ]);

    const gMap = new Map((grailRows ?? []).map((g) => [g.id, g]));
    const sMap = new Map((scanRows  ?? []).map((s) => [s.id, s]));
    const pMap = new Map(
      ((sellerRows ?? []) as Array<{ id: string; handle: string; display_name: string | null; avatar: string }>)
        .map((p) => [p.id, p] as const)
    );

    const enriched: GrailMatchRow[] = rows.map((r) => {
      const g = gMap.get(r.grail_id);
      const s = sMap.get(r.scan_id);
      const p = pMap.get(r.seller_id);
      return {
        id: r.id,
        grail_id: r.grail_id,
        scan_id: r.scan_id,
        seller_id: r.seller_id,
        matched_price: r.matched_price,
        over_budget: r.over_budget,
        seen: r.seen,
        dismissed: r.dismissed,
        created_at: r.created_at,
        grail_item: g?.item ?? "Your grail",
        scan_title: s?.title ?? "Unknown piece",
        scan_brand: s?.brand ?? null,
        scan_era: s?.era ?? null,
        scan_photo: (s?.photos && s.photos.length > 0) ? s.photos[0] : null,
        scan_img: (s?.img ?? "olive") as string,
        seller_handle: p?.handle ?? "someone",
        seller_display_name: p?.display_name ?? null,
        seller_avatar: ((p?.avatar ?? "cream") as SwatchKind),
      };
    });
    setMatches(enriched);
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const dismiss = async (matchId: string) => {
    setMatches((m) => m.filter((x) => x.id !== matchId));
    await supabase.from("grail_matches").update({ dismissed: true }).eq("id", matchId);
  };

  const markAllSeen = async () => {
    if (!user) return;
    const unseen = matches.filter((m) => !m.seen).map((m) => m.id);
    if (unseen.length === 0) return;
    await supabase.from("grail_matches").update({ seen: true }).in("id", unseen);
    setMatches((m) => m.map((x) => ({ ...x, seen: true })));
  };

  return { matches, loading, dismiss, markAllSeen, refresh: load };
}

export function useMyProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setProfile(null); setLoading(false); return; }
    let cancelled = false;
    supabase.from("profiles").select("*").eq("id", user.id).maybeSingle().then(({ data }) => {
      if (cancelled) return;
      if (data) setProfile(data as Profile);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user]);

  return { profile, loading };
}

/**
 * Live count of unseen, undismissed grail matches for the signed-in user.
 * Subscribes to realtime inserts so a toast can fire and the dock badge
 * updates the moment a seller posts a matching scan.
 */
export function useUnseenGrailMatches(onNew?: (row: { id: string; grail_id: string }) => void) {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  const refresh = async () => {
    if (!user) { setCount(0); return; }
    const { count: c } = await supabase
      .from("grail_matches")
      .select("id", { count: "exact", head: true })
      .eq("grail_owner_id", user.id)
      .eq("seen", false)
      .eq("dismissed", false);
    setCount(c ?? 0);
  };

  useEffect(() => {
    if (!user) { setCount(0); return; }
    void refresh();
    const key = `grail-matches-${user.id}-${Math.random().toString(36).slice(2, 8)}`;
    registerChannel(key, () =>
      supabase
        .channel(key)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "grail_matches",
            filter: `grail_owner_id=eq.${user.id}`,
          },
          (payload) => {
            setCount((c) => c + 1);
            if (onNew && payload.new) {
              onNew(payload.new as { id: string; grail_id: string });
            }
          },
        )
        .subscribe()
    );
    return () => { unregisterChannel(key); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { count, refresh };
}

// A public grail row joined with its owner's profile snapshot.
export interface PublicGrailRow {
  id: string;
  user_id: string;
  item: string;
  size: string | null;
  max_price: number | null;
  hits: number;
  created_at: string;
  owner_handle: string;
  owner_display_name: string | null;
  owner_avatar: SwatchKind;
}

/**
 * Browse other users' want-lists. Excludes the signed-in user's own grails so
 * the discovery feed feels external.
 */
export function usePublicGrails() {
  const { user } = useAuth();
  const [rows, setRows] = useState<PublicGrailRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      let q = supabase
        .from("grails")
        .select("id,user_id,item,size,max_price,hits,created_at")
        .order("hits", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(60);
      if (user) q = q.neq("user_id", user.id);
      const { data: grailRows } = await q;
      if (cancelled || !grailRows || grailRows.length === 0) {
        if (!cancelled) { setRows([]); setLoading(false); }
        return;
      }
      const ownerIds = Array.from(new Set(grailRows.map((g) => g.user_id)));
      const { data: ownerRows } = await (supabase as any)
        .from("profiles_public")
        .select("id,handle,display_name,avatar")
        .in("id", ownerIds);
      const oMap = new Map(
        ((ownerRows ?? []) as Array<{ id: string; handle: string; display_name: string | null; avatar: string }>)
          .map((p) => [p.id, p] as const)
      );
      const enriched: PublicGrailRow[] = grailRows.map((g) => {
        const o = oMap.get(g.user_id);
        return {
          id: g.id,
          user_id: g.user_id,
          item: g.item,
          size: g.size,
          max_price: g.max_price,
          hits: g.hits,
          created_at: g.created_at,
          owner_handle: o?.handle ?? "someone",
          owner_display_name: o?.display_name ?? null,
          owner_avatar: ((o?.avatar ?? "cream") as SwatchKind),
        };
      });
      if (!cancelled) { setRows(enriched); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  return { rows, loading };
}
